import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

import { useIsMobile } from '@/hooks/use-mobile'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PortalContent } from '@/components/portal/portal-content.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { useGetContentByPage } from '@/features/document-contents/hooks/use-get-content-by-page.ts'
import { useGetDocument } from '@/features/documents/hooks/use-get-document.ts'
import { Document } from '@/features/documents/types.ts'

import { ChangeStatusDialog } from './-components/change-status-dialog.tsx'
import { DesktopDocumentView } from './-components/desktop-document-view.tsx'
import { DocumentActionsMenu } from './-components/document-actions-menu.tsx'
import { DocumentActionsToolbar } from './-components/document-actions-toolbar.tsx'
import { MobileDocumentView } from './-components/mobile-document-view.tsx'
import { PreviewTabContent } from './-components/preview-tab-content.tsx'
import { useDocumentActions } from './-components/use-document-actions.tsx'
import { useDocumentNavigation } from './-components/use-document-navigation.ts'

const documentViewSearch = z
  .object({
    pageNum: z.coerce
      .number()
      .transform((v) => v ?? 1)
      .optional(),
    previewMode: z
      .enum(['image', 'markdown'])
      .transform((v) => v ?? 'image')
      .optional(),
    mode: z
      .enum(['view', 'modify'])
      .transform((v) => v ?? 'view')
      .optional(),
    tab: z
      .enum(['details', 'preview', 'attachments', 'logs'])
      .default('preview')
      .optional(),
  })
  .optional()

type DocumentViewSearch = NonNullable<z.infer<typeof documentViewSearch>>

export const Route = createFileRoute('/_authenticated/documents/$documentId/')({
  component: DocumentView,
  validateSearch: documentViewSearch,
})

function DocumentView() {
  const { documentId } = Route.useParams()
  const isMobile = useIsMobile()

  const search = {
    mode: 'view',
    pageNum: 1,
    previewMode: 'image',
    tab: isMobile ? 'details' : 'preview',
    ...Route.useSearch(),
  } as DocumentViewSearch

  const { mode, tab, pageNum = 1, previewMode = 'image' } = search ?? {}

  const [isPollingEnabled, setPollingEnabled] = useState(false)

  const documentQuery = useGetDocument(
    { documentId },
    { refetchInterval: isPollingEnabled ? 2000 : 0 }
  )
  const document = documentQuery?.data?.data

  useEffect(() => {
    setPollingEnabled(document?.status === 'processing')
  }, [document?.status])

  const getDocumentContentQuery = useGetContentByPage(
    { documentId: document?.id ?? '', pageNum: pageNum },
    { enabled: previewMode === 'markdown' }
  )

  const markdown = getDocumentContentQuery.data?.success
    ? getDocumentContentQuery.data?.data?.content
    : ''

  // Custom hooks for business logic
  const {
    changeStatusModalCtrl,
    handleChangeStatusBtnClick,
    handleDeleteBtnClick,
    handleDownloadBtnClick,
  } = useDocumentActions(document)

  const { goPrev, goNext, setPreviewMode, handleTabChange } =
    useDocumentNavigation(search, document, isMobile)

  // Shared preview content
  const previewContent = (
    <PreviewTabContent
      document={document}
      previewMode={previewMode}
      pageNum={pageNum}
      markdown={markdown}
      getDocumentContentQuery={getDocumentContentQuery}
      onPrevPage={goPrev}
      onNextPage={goNext}
      onPreviewModeChange={setPreviewMode}
      isMobile={isMobile}
    />
  )

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Documenti', to: '/documents/browse' },
          { name: document?.name ?? documentId },
        ]}
      />

      <QuerySpinner query={documentQuery} wrapperClassName="py-10">
        <div className="flex h-full flex-col">
          {document?.status !== 'processing' && (
            <PortalContent id="header-actions">
              {isMobile ? (
                <DocumentActionsMenu
                  documentId={documentId}
                  search={search}
                  mode={mode || 'view'}
                  document={document}
                  onChangeStatus={handleChangeStatusBtnClick}
                  onDownload={handleDownloadBtnClick}
                  onDelete={handleDeleteBtnClick}
                />
              ) : (
                <DocumentActionsToolbar
                  documentId={documentId}
                  search={search}
                  mode={mode || 'view'}
                  document={document}
                  onChangeStatus={handleChangeStatusBtnClick}
                  onDownload={handleDownloadBtnClick}
                  onDelete={handleDeleteBtnClick}
                />
              )}
            </PortalContent>
          )}

          {isMobile ? (
            <MobileDocumentView
              document={document}
              mode={mode || 'view'}
              tab={tab || 'details'}
              onTabChange={handleTabChange}
              previewContent={previewContent}
            />
          ) : (
            <DesktopDocumentView
              document={document}
              mode={mode || 'view'}
              tab={tab || 'preview'}
              onTabChange={handleTabChange}
              previewContent={previewContent}
            />
          )}
        </div>
      </QuerySpinner>

      <ChangeStatusDialog
        isOpen={changeStatusModalCtrl.isOpen}
        onClose={changeStatusModalCtrl.close}
        document={document as Document}
      />
    </>
  )
}
